import React, { useEffect, useMemo, useState } from "react";
import DashboardPage from "../../../layout";
import PaginationSearch from "../components/PaginationAndSearch";
import { toast, ToastContainer } from "react-toastify";
import { useDebounce } from "../hooks/debounce";
import URL_MAPPING from "../../../routes/constants";
import { useNavigate } from "react-router-dom";
import LMDTerritoryTable from "../components/LMDTerritoryTable";
import { listLmdTerritories } from "../api/territory";

const LMDTerritory = () => {
  const [tableData, setTableData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [refetch, setRefetch] = useState(false);

  const navigate = useNavigate();

  const debounce = useDebounce(search);

  const getTableData = async () => {
    try {
      setLoading(true);
      const paramsObject: {
        search?: string;
      } = {};
      if (search.length > 0) paramsObject.search = search;
      const response = await listLmdTerritories(page, 20, paramsObject);
      
      // Handle different response structures
      let territories = [];
      let total = 0;
      let currentPage = page;
      let pageSize = 20;

      if (Array.isArray(response)) {
        // Direct array response
        territories = response;
        total = response.length;
      } else if (response?.items && Array.isArray(response.items)) {
        // Response with items property
        territories = response.items;
        total = response.total || response.items.length;
        currentPage = response.page || page;
        pageSize = response.size || 20;
      } else if (response?.data && Array.isArray(response.data)) {
        // Response with data property
        territories = response.data;
        total = response.total || response.data.length;
        currentPage = response.page || page;
        pageSize = response.size || 20;
      } else {
        toast.error("No data received or malformed response");
        console.warn("No data received or malformed response:", response);
        setTableData({ items: [], page: 1, size: 20, total: 0 });
        return;
      }

      // Transform territories into table rows
      // Create separate rows for employees and sales offices
      const flattenedItems: any[] = [];
      territories.forEach((territory: any) => {
        const { territory_id, territory_name, assigned_employees = [], assigned_sales_offices = [] } = territory;

        // Create rows for each employee (with sales office as "-")
        if (assigned_employees.length > 0) {
          assigned_employees.forEach((emp: any) => {
            flattenedItems.push({
              id: `${territory_id}-emp-${emp.emp_id}`,
              territory_id: territory_id,
              territory_name,
              emp_id: emp.emp_id,
              emp_name: emp.name_eng || emp.name_arb || "-",
              sales_office_id: "-",
              sales_office_name: "-",
            });
          });
        }

        // Create rows for each sales office (with employee as "-")
        if (assigned_sales_offices.length > 0) {
          assigned_sales_offices.forEach((office: any) => {
            flattenedItems.push({
              id: `${territory_id}-office-${office.sales_office_id}`,
              territory_id: territory_id,
              territory_name,
              emp_id: "-",
              emp_name: "-",
              sales_office_id: office.sales_office_id,
              sales_office_name: office.sales_office_text || "-",
            });
          });
        }

        // If territory has neither employees nor sales offices, show one row with both as "-"
        if (assigned_employees.length === 0 && assigned_sales_offices.length === 0) {
          flattenedItems.push({
            id: territory_id,
            territory_id: territory_id,
            territory_name,
            emp_id: "-",
            emp_name: "-",
            sales_office_id: "-",
            sales_office_name: "-",
          });
        }
      });

      setTableData({
        items: flattenedItems,
        page: currentPage,
        size: pageSize,
        total: total,
      });
      setPage(currentPage);
    } catch (error: any) {
      console.error("Failed to fetch LMD territory data:", error);
      if (error?.response?.status === 404) {
        toast.error("Route not found, Please try again later");
      } else if (error?.response?.status === 500) {
        toast.error("Error while fetching the LMD territory");
      } else {
        const detail = error?.response?.data?.detail;
        if (Array.isArray(detail)) {
          detail.forEach((err) => toast.error(err.msg));
        } else {
          toast.error(detail?.msg || detail || "An unexpected error occurred.");
        }
      }
      setTableData({ items: [], page: 1, size: 20, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationNext = () => {
    if (loading) return;
    setPage((prev) => prev + 1);
  };

  const handlePaginationPrev = () => {
    if (loading) return;
    setPage((prev) => Math.max(prev - 1, 1));
  };

  const totalPages = useMemo(() => {
    if (!tableData?.total || !tableData?.size) return 0;
    return Math.ceil(tableData.total / tableData.size);
  }, [tableData?.total, tableData?.size]);

  useEffect(() => {
    getTableData();
  }, [page, debounce, refetch]);

  return (
    <DashboardPage>
      <ToastContainer />
      <PaginationSearch
        page={page}
        size={totalPages}
        handlePaginationNext={handlePaginationNext}
        handlePaginationPrev={handlePaginationPrev}
        search={search}
        setSearch={setSearch}
        placeholder="Search By Employee ID or Territory Name"
      />
      <div className="mb-4 px-5 flex items-end justify-between">
        <h3 className="text-xl font-bold text-gray-700">LMD Territory</h3>
        <div className="flex gap-3">
          <button
            onClick={() => navigate(URL_MAPPING.TERRITORYMAPVIEW)}
            className="bg-teal-500 text-white px-6 py-2 rounded hover:bg-teal-600 flex items-center gap-2"
          >
            Create Territory
          </button>
          <button
            onClick={() => navigate(URL_MAPPING.ASSIGNLMDTERRITORY)}
            className="bg-teal-500 text-white px-6 py-2 rounded hover:bg-teal-600 flex items-center gap-2"
          >
            Assign Territory
          </button>
        </div>
      </div>
      <LMDTerritoryTable
        tableData={tableData}
        loading={loading}
        refetch={refetch}
        setRefetch={setRefetch}
      />
    </DashboardPage>
  );
};

export default LMDTerritory;

